import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  styled,
  alpha,
  Chip,
  Collapse,
  IconButton,
  Grid,
  Divider,
  Dialog,
} from "@mui/material";
import {
  Schedule,
  CheckCircle,
  Cancel,
  Payment,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Close as CloseIcon,
} from "@mui/icons-material";
import { formatCurrency, formatDateToIST } from "./transactionUtils";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "18px 16px",
  "&.header": {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[200],
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: theme.shadows[3],
  borderRadius: theme.spacing(2),
  minHeight: 800,
  maxHeight: 850,
  overflow: "auto",
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const statusStr = status != null ? String(status).toLowerCase() : "";
  const configs = {
    pending: {
      bg: "#FFB74D",
      text: "#FFFFFF",
      icon: <Schedule sx={{ fontSize: "16px !important" }} />,
    },
    approved: {
      bg: "#66BB6A",
      text: "#FFFFFF",
      icon: <CheckCircle sx={{ fontSize: "16px !important" }} />,
    },
    rejected: {
      bg: "#EF5350",
      text: "#FFFFFF",
      icon: <Cancel sx={{ fontSize: "16px !important" }} />,
    },
  };
  const config = configs[statusStr] || {
    bg: theme.palette.grey[400],
    text: theme.palette.common.white,
    icon: null,
  };

  return {
    backgroundColor: config.bg,
    color: config.text,
    fontWeight: 600,
    textTransform: "capitalize",
    borderRadius: theme.shape.borderRadius,
    height: "auto",
    padding: "4px 10px",
    minWidth: "auto",
    "& .MuiChip-icon": {
      color: config.text,
      marginLeft: "4px",
      marginRight: "4px",
    },
    "& .MuiChip-label": {
      padding: "0 4px",
      fontSize: "0.875rem",
      color: config.text,
      fontWeight: 600,
    },
  };
});

const ADVANCE_PAYMENT_FIELD_LABELS = {
  transaction_id: "Transaction ID",
  transaction_reference_no: "Transaction Reference No.",
  upi_id_used: "UPI ID",
  payment_screenshot: "Payment Screenshot",
  sender_bank_name: "Bank Name",
  payment_confirmation_file: "Payment Confirmation File",
  cheque_number: "Cheque Number",
  bank_name_branch: "Bank Name / Branch",
  cheque_date: "Cheque Date",
  cheque_amount: "Cheque Amount",
  cheque_image: "Cheque Image",
  cash_receipt_number: "Cash Receipt No.",
  receiver_name: "Receiver Name (Staff)",
};

const getAdvancePaymentFieldRows = (entry = {}) => {
  const fields = entry?.fields;
  if (!fields || typeof fields !== "object") return [];
  const skipKeys = new Set([
    "id",
    "created_at",
    "updated_at",
    "is_deleted",
    "sender_bank_name",
    "bank_name_branch",
  ]);
  return Object.entries(fields)
    .filter(([key, val]) => !skipKeys.has(key) && val != null && String(val).trim() !== "")
    .map(([key, val]) => ({
      key,
      label:
        ADVANCE_PAYMENT_FIELD_LABELS[key] ||
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      value: String(val),
    }));
};

const formatOrderDateTime = (value) => {
  if (!value) return "N/A";
  try {
    return new Date(value).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const getEntryBankName = (entry = {}) =>
  entry?.sender_bank_name ||
  entry?.fields?.sender_bank_name ||
  entry?.fields?.bank_name_branch ||
  "";

const getEntryNote = (entry = {}, order = null) => {
  if (entry?.comment != null && String(entry.comment).trim() !== "") {
    return String(entry.comment).trim();
  }
  const notes = entry?.order?.notes ?? order?.notes;
  if (notes != null && String(notes).trim() !== "") {
    return String(notes).trim();
  }
  return "";
};

const AdvancePaymentEntryCard = ({ entry, index, theme, order = null }) => {
  const fieldRows = getAdvancePaymentFieldRows(entry);
  const recordedBy =
    entry.user?.full_name ||
    [entry.user?.first_name, entry.user?.last_name].filter(Boolean).join(" ") ||
    "—";
  const [previewUrl, setPreviewUrl] = useState(null);
  const isFullyPaid = entry.remaining_amount === 0 || entry.remaining_amount === 0.0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.warning.main, 0.04),
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5, gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Entry #{index + 1}
          {entry.is_full_payment ? " · Full payment" : " · Advanced payment"}
        </Typography>
        <Chip
          size="small"
          label={entry.method?.name || entry.payment_method || "—"}
          color="warning"
          variant="outlined"
        />
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>{isFullyPaid ? "Remaining Paid Amount" : "Advance Paid Amount"}:</strong>{" "}
            {formatCurrency(entry.advance_amount)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Pending Amount:</strong> {formatCurrency(entry.remaining_amount)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Bank Name:</strong> {getEntryBankName(entry) || "N/A"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Recorded By:</strong> {recordedBy}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Transaction ID:</strong>{" "}
            {entry.transaction_id ||
              entry.transaction_reference_no ||
              entry.fields?.transaction_reference_no ||
              entry.fields?.transaction_id ||
              "—"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Payment Date:</strong> {formatOrderDateTime(entry.created_at)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Updated At:</strong> {formatOrderDateTime(entry.updated_at)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Note:</strong> {getEntryNote(entry, order) || "N/A"}
          </Typography>
        </Grid>
      </Grid>

      {fieldRows.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Payment method details
          </Typography>
          <Grid container spacing={1}>
            {fieldRows.map((row) => (
              <Grid item xs={12} sm={6} key={`${entry.id}-${row.key}`}>
                {row.key === "payment_screenshot" ||
                row.key === "payment_confirmation_file" ||
                row.key === "cheque_image" ? (
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
                  >
                    <strong>{row.label}:</strong>
                    {row.value ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setPreviewUrl(row.value)}
                        sx={{
                          textTransform: "none",
                          border: "1px solid #1e293b",
                          borderRadius: "8px",
                          px: "18px",
                          py: "6px",
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#1e293b",
                          bgcolor: "#fff",
                          "&:hover": {
                            border: "1px solid #1e293b",
                            bgcolor: "#f1f5f9",
                          },
                        }}
                      >
                        View
                      </Button>
                    ) : null}
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    <strong>{row.label}:</strong> {row.value}
                  </Typography>
                )}
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <Dialog
        open={Boolean(previewUrl)}
        onClose={() => setPreviewUrl(null)}
        maxWidth={false}
        PaperProps={{ sx: { borderRadius: "12px", p: 2, maxWidth: "90vw", maxHeight: "90vh" } }}
        BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.6)" } }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setPreviewUrl(null)}
            size="small"
            sx={{ position: "absolute", top: 6, right: 6, zIndex: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box
            component="img"
            src={previewUrl || ""}
            alt="Payment attachment"
            sx={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: "10px" }}
          />
        </Box>
      </Dialog>
    </Paper>
  );
};

const PaymentDetailPanel = ({ payment, theme }) => {
  const raw = payment.raw || payment;
  const entry = {
    ...raw,
    method: payment.method || raw.method,
    fields: payment.fields || raw.fields || {},
    advance_amount: payment.advance_amount ?? raw.advance_amount,
    transaction_id: payment.transaction_id ?? raw.transaction_id,
    transaction_reference_no:
      payment.transaction_reference_no ?? raw.transaction_reference_no,
    payment_method: payment.payment_method,
    created_at: payment.created_at ?? raw.created_at,
    updated_at: raw.updated_at,
    remaining_amount: raw.remaining_amount,
    is_full_payment: raw.is_full_payment,
    user: raw.user,
    comment: raw.comment,
    id: payment.id ?? raw.id,
  };

  return (
    <Paper
      elevation={2}
      sx={{
        m: 2,
        backgroundColor: "background.paper",
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        borderRadius: 3,
        p: 2.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Payment sx={{ mr: 1, color: theme.palette.warning.main }} />
        <Typography variant="h6" fontWeight={600}>
          Payment Details (1)
        </Typography>
      </Box>
      <AdvancePaymentEntryCard entry={entry} index={0} theme={theme} order={payment.order} />
    </Paper>
  );
};

const TransactionTable = ({
  payments = [],
  isLoading = false,
  statusFilter = "pending",
  updatingId = null,
  onApprove,
  onReject,
  canApprove = false,
  canReject = false,
}) => {
  const theme = useTheme();
  const [expandedId, setExpandedId] = useState(null);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 280, p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <StyledTableContainer component={Paper}>
      <Table stickyHeader size="small">
        <TableHead sx={{ position: "sticky", top: 0, zIndex: 2 }}>
          <TableRow>
            <StyledTableCell className="header" align="center" sx={{ width: 72 }}>
              Sr. No.
            </StyledTableCell>
            <StyledTableCell className="header">Order ID</StyledTableCell>
            <StyledTableCell className="header">Customer</StyledTableCell>
            <StyledTableCell className="header">Payment Method</StyledTableCell>
            <StyledTableCell className="header">Amount</StyledTableCell>
            <StyledTableCell className="header" align="center">
              Status
            </StyledTableCell>
            <StyledTableCell className="header" align="center" sx={{ minWidth: 220 }}>
              Actions
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!payments.length ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No transactions found for the selected filters.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            payments.map((payment, index) => {
              const statusKey = String(payment.status || "pending").toLowerCase();
              const rowId = payment.id ?? payment.order_id;
              const isExpanded = expandedId === rowId;
              const isUpdating = updatingId != null && String(updatingId) === String(rowId);

              return (
                <React.Fragment key={rowId}>
                  <StyledTableRow
                    hover
                    onClick={() => setExpandedId(isExpanded ? null : rowId)}
                    sx={{
                      backgroundColor:
                        index % 2 === 1
                          ? theme.palette.action.hover
                          : theme.palette.background.paper,
                    }}
                  >
                    <StyledTableCell align="center">
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {index + 1}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25 }}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25, flexShrink: 0 }}
                        >
                          {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                        </IconButton>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <Typography variant="body2" fontWeight={600}>
                            {payment.order_id ?? payment.order_number ?? "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ textAlign: "center" }}>
                            {formatDateToIST(payment.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">{payment.farmer_name ?? "—"}</Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">{payment.payment_method ?? "—"}</Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(payment.advance_amount)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <StatusChip
                        size="small"
                        label={String(payment.status || "pending")}
                        status={statusKey}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {statusKey === "pending" ? (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <Button
                            size="small"
                            color="success"
                            variant="outlined"
                            disabled={isUpdating || !canApprove}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (canApprove) onApprove?.(payment);
                            }}
                            sx={{ textTransform: "none", fontWeight: 600, opacity: !canApprove ? 0.6 : 1 }}
                          >
                            {isUpdating ? <CircularProgress size={18} /> : "Accepted"}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={isUpdating || !canReject}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (canReject) onReject?.(payment);
                            }}
                            sx={{ textTransform: "none", fontWeight: 600, opacity: !canReject ? 0.6 : 1 }}
                          >
                            Rejected
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </StyledTableCell>
                  </StyledTableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <PaymentDetailPanel payment={payment} theme={theme} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default TransactionTable;
